import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import {
  getMCCTaskExportMessagesDetails,
  getExportMessagesDownloadURL,
} from "graphql/queries.js";

// react-bootstrap components
import { Container, Row, Col, Spinner, Card, Button } from "react-bootstrap";
import { getTaskStatusLabel } from "utilities/uiMappingUtilities";

function ExportMessagesTaskDetails() {
  const { cloudTaskId } = useParams();
  const history = useHistory();
  const notify = useContext(NotificationAlertContext);
  const [taskData, setTaskData] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const link = document.createElement("a");

  // Local mappings
  const outcomeOptions = [
    { value: 0, label: "Success" },
    { value: 1, label: "Failure" },
  ];

  const getOutcomeLabel = (value) =>
    outcomeOptions.find((o) => o.value === value)?.label ?? value ?? "-";

  // Fetch task details using GraphQL
  const fetchExportMessagesTaskDetails = async () => {
    try {
      setLoading(true);

      const response = await API.graphql(
        graphqlOperation(getMCCTaskExportMessagesDetails, {
          cloud_task_id: cloudTaskId,
        }),
      );

      const result = response?.data?.getMCCTaskExportMessagesDetails;

      if (result?.success) {
        const body = result.body;
        setTaskData(body);

        setMessage(body?.message || body?.error || "");

        notify("success", "Task details loaded successfully");
        return true;
      } else {
        throw new Error(result.message || "Failed to fetch task details");
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      history.push("/admin/mirth-tasks");
      notify("danger", `Failed to load task details: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch task details using GraphQL
  const fetchExportMessagesDownloadURL = async () => {
    try {
      notify("warning", "Download will start shortly");

      const response = await API.graphql(
        graphqlOperation(getExportMessagesDownloadURL, {
          cloud_task_id: cloudTaskId,
        }),
      );

      const result = response?.data?.getExportMessagesDownloadURL;

      if (result?.success) {
        const body = result.body;
        link.setAttribute("href", body?.download_url);
        link.click();

        notify("success", "Download started successfully");
        return true;
      } else {
        throw new Error("Failed to fetch export message download URL");
      }
    } catch (error) {
      console.error("Error fetching export message download URL:", error);
      notify("danger", error.message);
      return false;
    }
  };

  useEffect(() => {
    link.style.display = "none";
    document.body.appendChild(link);
    async function fetchData() {
      if (cloudTaskId) {
        const isSuccess = await fetchExportMessagesTaskDetails();
        if (!isSuccess) {
          notify("danger");
        }
      } else {
        notify("danger", "Missing required parameters for task details");
        history.push("/admin/mirth-tasks");
      }
    }
    fetchData();

    // Clean up link on component unmount
    return () => {
      document.body.removeChild(link);
    };
  }, [cloudTaskId]);

  if (loading) return <Spinner animation="grow" />;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Cloud Task - Export Messages Details</h2>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <Row>
                  <Col md="6">
                    <h5>Task Id: {cloudTaskId}</h5>
                    <h5>Client Name: {taskData?.client_name}</h5>
                    <h5>
                      Environment Name: {taskData?.client_environment_name}
                    </h5>
                    <h5>Status: {getTaskStatusLabel(taskData?.status, "exportmessages")}</h5>
                    <h5>Outcome: {getOutcomeLabel(taskData?.outcome)}</h5>
                    <h5>Message: {message}</h5>
                    {taskData?.destination && (
                      <Button
                        className="btn-outline"
                        onClick={() => {
                          fetchExportMessagesDownloadURL();
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ExportMessagesTaskDetails;
